import type { MediaDto } from '@/lib/sanity/dto';
import { IconCarProfile } from '@/components/icons/IconCarProfile';
import { IconImagesSquare } from '@/components/icons/IconImagesSquare';
import { UiImage } from '@/components/ui/UiImage';
import { cn } from '@/lib/utils/tw-merge';
import type { VehicleAvailability } from './vehicle.types';
import { VehicleAvailabilityLabel } from './VehicleAvailabilityLabel';

type VehicleMediaProps = {
    image: MediaDto | null;
    sizes: string;
    variant: 'card' | 'list';
    badges?: string[];
    /** Shows the photo counter; the list row leaves it out. */
    imageCount?: number;
    availability?: VehicleAvailability;
    eager?: boolean;
    className?: string;
};

/**
 * The 4:3 photo frame of a vehicle. Badges, counter and scrim are DOM overlays —
 * never baked into the image, Google Vehicle Ads rejects photos carrying them.
 */
export function VehicleMedia({
    image,
    sizes,
    variant,
    badges = [],
    imageCount = 0,
    availability,
    eager = false,
    className,
}: VehicleMediaProps) {
    const showAvailability = availability && availability.state !== 'available';

    return (
        <div className={cn('vehicle-media', `vehicle-media--${variant}`, !image && 'vehicle-media--empty', className)}>
            {image ? (
                <>
                    {/* The card's title names the vehicle; the photo adds no text of its own. */}
                    <UiImage image={image} sizes={sizes} alt="" loading={eager ? 'eager' : undefined} />
                    <div className="vehicle-media__scrim" />

                    {badges.length ? (
                        <div className="vehicle-media__badges">
                            {badges.map((badge) => (
                                <span key={badge} className="vehicle-chip label-xs">
                                    {badge}
                                </span>
                            ))}
                        </div>
                    ) : null}

                    {imageCount > 0 ? (
                        <span className="vehicle-chip vehicle-media__count label-xs">
                            <IconImagesSquare width={15} height={15} aria-hidden="true" />
                            {imageCount}
                            <span className="sr-only"> Fotos</span>
                        </span>
                    ) : null}
                </>
            ) : (
                <>
                    <IconCarProfile className="vehicle-media__placeholder" aria-hidden="true" />
                    <span className="label-xs">Fotos folgen</span>
                </>
            )}

            {showAvailability ? (
                <VehicleAvailabilityLabel availability={availability} className="availability--on-photo" />
            ) : null}
        </div>
    );
}
